// Helpers for the storage-based vault plans.

export const GIGABYTE = 1024 ** 3;

// Whether the vault is on a storage plan (legacy vaults have no limit).
export const hasStoragePlan = (vault: { storageLimit: number }): boolean =>
  vault.storageLimit > 0;

export const storageRemaining = (vault: {
  storageLimit: number;
  totalStorage: number;
}): number => {
  if (!hasStoragePlan(vault)) return Number.POSITIVE_INFINITY;
  return Math.max(vault.storageLimit - vault.totalStorage, 0);
};

export const isOutOfStorage = (vault: {
  storageLimit: number;
  totalStorage: number;
}): boolean => hasStoragePlan(vault) && storageRemaining(vault) <= 0;

// Would adding `bytes` exceed the vault's plan?
export const storageFits = (
  vault: { storageLimit: number; totalStorage: number },
  bytes: number,
): boolean => storageRemaining(vault) >= bytes;

// "23.3 GB", "512 MB", "1.02 TB" — short human-readable byte size.
export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 GB';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** exponent;
  const rounded =
    value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[exponent]}`;
};

// The API renders out-of-storage as error_code 507_01 with details
// 'out_of_storage' (HTTP 507). Detect it in any of the shapes our
// networking layer surfaces errors.
export const isOutOfStorageError = (err: unknown): boolean => {
  const anyErr = err as any;
  if (!anyErr) return false;
  if (anyErr.response?.status === 507 || anyErr.status === 507) return true;
  const errors = anyErr.errors ?? anyErr.response?.data?.errors ?? [];
  return (
    Array.isArray(errors) &&
    errors.some(
      (e: any) =>
        e?.errorCode === 50701 ||
        e?.error_code === 50701 ||
        e?.details === 'out_of_storage',
    )
  );
};
